import { selectCurrentLayout, selectLayoutSection } from '@src/components/AppLayout/AppLayoutSlice';
import { DEFAULT_LAYOUTS } from '@src/components/AppLayout/Layouts';
import { isConditionalLayoutSpecVisible } from '@src/components/AppLayout/Layouts/conditionalLayoutSpecs';
import { DEFAULT_LAYOUT_ID } from '@src/components/AppLayout/Layouts/tokens';
import { LayoutSectionBreadcrumb } from '@src/components/AppLayout/LayoutSectionBreadcrumb';
import {
  useLayoutSpecApiAccess,
  useLayoutSpecsQuery,
  type LayoutSpecResponse,
} from '@src/components/AppLayout/LayoutSpecs/LayoutSpecSlice';
import { AppLayoutPreview } from '@src/components/AppLayoutPreview/AppLayoutPreview';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import { toNavGroupName } from '@src/hooks/useSpacesNavItems';
import { useUser } from '@src/hooks/useUser';
import { useAppStateSelector } from '@src/providers/AppStateProvider';
import type { LayoutSection, LayoutSpec } from '@src/types/LayoutConfig';
import {
  Box,
  Button,
  ButtonVariants,
  DEVICE_TYPE_BREAKPOINTS,
  DialogFooterWrapper,
  Flex,
  FormControlSizes,
  FormGroup,
  Grid,
  IconButton,
  IconName,
  Input,
  MixpanelEvent,
  MixpanelEventProperty,
  Modal,
  PopperHeader,
  Stack,
  Text,
  ToggleGroup,
  Tooltip,
  useEventListener,
  useMixpanel,
  useSecuritiesContext,
  VStack,
  type DeviceType,
  type UseDisclosureReturn,
} from '@talos/kyoko';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LayoutOptionType, LayoutOptionWrapper } from './styles';

type TemplateFilter = 'all' | 'own' | 'shared' | 'allUsers';

export function ManageSpaceModal({
  onAddNewSpace,
  layoutSection,
  onEditTemplate,
  onUpdateSpace,
  initialLayoutSpec,
  initialGroup,
  existingGroups = [],
  sectionLayoutIdsAndLabels = [],
  ...disclosure
}: {
  onAddNewSpace?: (newSpaceName: string, selectedTemplate: LayoutSpec, deviceType: DeviceType, group?: string) => void;
  onEditTemplate: (config: LayoutSpecResponse) => void;
  onUpdateSpace?: (id: string, config: Partial<LayoutSpec>) => void;
  layoutSection: LayoutSection;
  /** When provided, the modal opens in edit mode with this layout's values */
  initialLayoutSpec?: LayoutSpec;
  /** Initial group used when adding a space from an existing group or grouped static page */
  initialGroup?: string;
  /** Existing group names in the current section for autocomplete suggestions */
  existingGroups?: string[];
  /** Spaces already in this section (id + label) — used to block duplicate space names */
  sectionLayoutIdsAndLabels?: { id: string; label: string }[];
} & UseDisclosureReturn) {
  const isEditMode = initialLayoutSpec != null;
  const [spaceNameTouched, setSpaceNameTouched] = useState(false);
  const layoutSpec = useAppStateSelector(selectCurrentLayout);
  const currentLayoutSection = useAppStateSelector(selectLayoutSection);
  const defaultLayouts = DEFAULT_LAYOUTS[layoutSection];
  const sectionLayoutSpec = currentLayoutSection === layoutSection ? layoutSpec : undefined;
  const copyOfLayoutSpec = useMemo(() => createLayoutCopy(sectionLayoutSpec), [sectionLayoutSpec]);
  const copyForEditLayout = useMemo(
    () => (initialLayoutSpec ? createLayoutCopy(initialLayoutSpec) : undefined),
    [initialLayoutSpec]
  );
  const defaultLayout = defaultLayouts[DEFAULT_LAYOUT_ID];
  const initialNewSpaceName =
    initialLayoutSpec?.label ?? (copyOfLayoutSpec ? `${copyOfLayoutSpec.label} copy` : defaultLayout.label);
  const [newSpaceName, setNewSpaceName] = useState(initialNewSpaceName);
  const [spaceNameError, setSpaceNameError] = useState(false);
  const [selectedDeviceType, setSelectedDeviceType] = useState<DeviceType>(initialLayoutSpec?.deviceType ?? 'all');
  const [selectedTemplate, setSelectedTemplate] = useState<LayoutSpec>(
    initialLayoutSpec ?? copyOfLayoutSpec ?? defaultLayout
  );
  const groupFromNav = initialLayoutSpec?.id != null ? (initialLayoutSpec.group ?? '') : (initialGroup ?? '');
  const [group, setGroup] = useState(groupFromNav);
  useEffect(() => {
    setGroup(groupFromNav);
  }, [groupFromNav]);
  const [groupTouched, setGroupTouched] = useState(false);

  const mixpanel = useMixpanel();
  const flags = useFeatureFlag();
  const { isAuthorized } = useRoleAuth();
  const securities = useSecuritiesContext();
  const user = useUser();
  const { canListAllSpaces, canEditAllSpaces } = useLayoutSpecApiAccess();
  const [templateFilter, setTemplateFilter] = useState<TemplateFilter>('all');

  const copyForTemplateGrid = isEditMode ? copyForEditLayout : copyOfLayoutSpec;

  const groupNameInvalid = useMemo(() => {
    if (group === '') {
      return false;
    }

    const inputSlug = toNavGroupName(group);
    // Only a literal empty string clears the group. Any non-empty input must produce a URL slug.
    if (inputSlug === '') {
      return true;
    }

    const collidesWithLayoutId = sectionLayoutIdsAndLabels.some(({ id }) => id === inputSlug);
    const collidesWithAnotherGroup = existingGroups.some(g => {
      // You can use the same group name as the current group, but not something else with the same URL slug.
      return g !== group && toNavGroupName(g) === inputSlug;
    });
    return collidesWithLayoutId || collidesWithAnotherGroup;
  }, [group, existingGroups, sectionLayoutIdsAndLabels]);

  const spaceNameDuplicate = useMemo(() => {
    const trimmed = newSpaceName.trim();
    if (trimmed === '') {
      return false;
    }
    const editingId = initialLayoutSpec?.id;
    return sectionLayoutIdsAndLabels.some(({ id, label }) => id !== editingId && label.trim() === trimmed);
  }, [newSpaceName, sectionLayoutIdsAndLabels, initialLayoutSpec?.id]);

  const handleSelectTemplate = useCallback(
    (template: LayoutSpec) => {
      setSelectedTemplate(template);
      setNewSpaceName(prev =>
        isEditMode && template.id === 'copy' ? prev : template.id === 'copy' ? `${template.label} copy` : template.label
      );
      mixpanel.track(MixpanelEvent.SelectLayoutTemplate, {
        [MixpanelEventProperty.LayoutSection]: layoutSection,
        [MixpanelEventProperty.Template]: template.label,
      });
    },
    [layoutSection, mixpanel, isEditMode]
  );

  const validateSpaceName = useCallback(() => {
    setSpaceNameError(newSpaceName === '');
    setSpaceNameTouched(true);
  }, [newSpaceName]);

  const handleAddNewSpace = useCallback(() => {
    validateSpaceName();
    setGroupTouched(true);
    if (newSpaceName === '' || spaceNameDuplicate || groupNameInvalid || onAddNewSpace == null) {
      return;
    }
    disclosure.close();
    onAddNewSpace(newSpaceName, selectedTemplate, selectedDeviceType, group === '' ? undefined : group);
  }, [
    disclosure,
    validateSpaceName,
    onAddNewSpace,
    selectedTemplate,
    newSpaceName,
    selectedDeviceType,
    group,
    groupNameInvalid,
    spaceNameDuplicate,
  ]);

  const handleUpdateSpace = useCallback(() => {
    validateSpaceName();
    setGroupTouched(true);
    if (
      newSpaceName === '' ||
      spaceNameDuplicate ||
      groupNameInvalid ||
      !initialLayoutSpec?.id ||
      onUpdateSpace == null
    ) {
      return;
    }
    disclosure.close();
    const templateId =
      selectedTemplate.id === 'copy'
        ? (initialLayoutSpec?.templateId ?? initialLayoutSpec?.id)
        : (selectedTemplate.templateId ?? selectedTemplate.id);
    onUpdateSpace(initialLayoutSpec.id, {
      label: newSpaceName,
      deviceType: selectedDeviceType,
      group: group === '' ? undefined : group,
      dockViewLayout: selectedTemplate.dockViewLayout,
      constraints: selectedTemplate.constraints,
      collapsablePanels: selectedTemplate.collapsablePanels,
      templateId,
    });
  }, [
    disclosure,
    validateSpaceName,
    onUpdateSpace,
    initialLayoutSpec,
    newSpaceName,
    selectedDeviceType,
    group,
    selectedTemplate,
    groupNameInvalid,
    spaceNameDuplicate,
  ]);

  const handleSubmit = useCallback(
    () => (isEditMode ? handleUpdateSpace() : handleAddNewSpace()),
    [isEditMode, handleUpdateSpace, handleAddNewSpace]
  );

  const handleEditTemplate = useCallback(
    (config: LayoutSpecResponse) => {
      disclosure.close();
      onEditTemplate(config);
    },
    [disclosure, onEditTemplate]
  );

  const { data: allLayoutSpecs } = useLayoutSpecsQuery();

  const filteredSharedLayoutSpecs = useMemo(() => {
    const layoutSpecs = allLayoutSpecs?.filter(spec => spec.Config.section === layoutSection);
    if (layoutSpecs == null) {
      return [];
    }
    const userFilteredLayoutSpecs =
      templateFilter === 'own'
        ? layoutSpecs.filter(config => config.User === user.Name)
        : templateFilter === 'shared'
          ? layoutSpecs.filter(config => config.User !== user.Name)
          : layoutSpecs;

    return userFilteredLayoutSpecs.filter(spec =>
      isConditionalLayoutSpecVisible(layoutSection, spec.Config.layoutSpec, {
        flags,
        isAuthorized,
        securities,
      })
    );
  }, [templateFilter, allLayoutSpecs, user.Name, layoutSection, flags, isAuthorized, securities]);

  const visibleDefaultLayouts = useMemo(
    () =>
      Array.from(
        new Map(
          Object.values(defaultLayouts)
            .filter(template =>
              isConditionalLayoutSpecVisible(layoutSection, template, {
                flags,
                isAuthorized,
                securities,
              })
            )
            .map(template => [template.id, template])
        ).values()
      ),
    [defaultLayouts, flags, isAuthorized, layoutSection, securities]
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  useEventListener(
    'scroll',
    () => {
      setIsScrolled((scrollRef.current?.scrollTop ?? 0) > 0);
    },
    scrollRef
  );

  return (
    <>
      <Modal {...disclosure} data-testid="manage-space-modal" width="1350px" height="min(90vh, 880px)">
        <Flex
          as="form"
          id="manage-space-form"
          onSubmit={e => {
            e.preventDefault();
            handleSubmit();
          }}
          overflow="hidden"
          flex="1 1 auto"
          flexOrder="2"
        >
          <Stack flex="0 1 auto" w="clamp(240px, 25%, 400px)">
            <Box m="spacing16">
              <Text as="h4" fontSize="fontSizeTitleH4" color="colorTextImportant" m={0}>
                {isEditMode ? 'Edit space' : 'Add new space'}
              </Text>
              <Text fontSize="fontSizeDefault" color="colorTextSubtle">
                {isEditMode
                  ? 'Update the name, device type or layout of this space'
                  : 'Add a new space to the current section'}
              </Text>
            </Box>
            <FormGroup
              mx="spacing16"
              label={isEditMode ? 'Space name' : 'New space name'}
              error={
                spaceNameTouched
                  ? spaceNameError
                    ? 'Name cannot be empty'
                    : spaceNameDuplicate
                      ? 'A space with this name already exists in this section.'
                      : undefined
                  : undefined
              }
            >
              <Input
                data-testid="manage-space-name-input"
                value={newSpaceName}
                onChange={e => setNewSpaceName(e.target.value)}
                onFocus={() => setSpaceNameError(false)}
                onBlur={() => {
                  setSpaceNameError(newSpaceName === '');
                  setSpaceNameTouched(true);
                }}
                placeholder="Enter a name for the space"
                invalid={spaceNameError || spaceNameDuplicate}
                touched={spaceNameTouched}
              />
            </FormGroup>
            <FormGroup
              mx="spacing16"
              label="Group"
              help="Assign this space to a named navigation group"
              error={
                groupTouched && groupNameInvalid ? 'Another group or space already uses this URL path.' : undefined
              }
            >
              <Input
                data-testid="manage-space-group-input"
                value={group}
                onChange={e => setGroup(e.target.value)}
                onBlur={() => setGroupTouched(true)}
                placeholder="Enter a group (optional)"
                invalid={groupNameInvalid}
                touched={groupTouched}
              />
            </FormGroup>
            <FormGroup mx="spacing16" label="Device type" help="The default device type for the space">
              <ToggleGroup
                data-testid="edit-space-device-type"
                defaultSelectedVariant={ButtonVariants.Priority}
                options={[
                  { tooltip: 'All devices', value: 'all', displayName: null, startIcon: IconName.Globe },
                  {
                    tooltip: `Widescreen (> 1920px)`,
                    value: 'widescreen',
                    displayName: null,
                    startIcon: IconName.DeviceDesktopWide,
                  },
                  {
                    tooltip: `Desktop (≤ ${DEVICE_TYPE_BREAKPOINTS.desktop}px)`,
                    value: 'desktop',
                    displayName: null,
                    startIcon: IconName.DeviceDesktop,
                  },
                  {
                    tooltip: `Laptop (≤ ${DEVICE_TYPE_BREAKPOINTS.laptop}px)`,
                    value: 'laptop',
                    displayName: null,
                    startIcon: IconName.DeviceLaptop,
                  },
                  {
                    tooltip: `Tablet (≤ ${DEVICE_TYPE_BREAKPOINTS.tablet}px)`,
                    value: 'tablet',
                    displayName: null,
                    startIcon: IconName.DeviceTablet,
                  },
                  {
                    tooltip: `Mobile (≤ ${DEVICE_TYPE_BREAKPOINTS.mobile}px)`,
                    value: 'mobile',
                    displayName: null,
                    startIcon: IconName.DeviceMobile,
                  },
                ]}
                value={selectedDeviceType}
                onChange={setSelectedDeviceType}
              />
            </FormGroup>
          </Stack>
          <Stack
            background="backgroundContent"
            flex="1 1 auto"
            overflow="auto"
            scrollbarGutter="stable both-edges"
            ref={scrollRef}
          >
            {isEditMode && initialLayoutSpec ? (
              <VStack p="spacing48" gap="spacing16" flex="1 1 auto" data-testid="edit-space-layout-preview">
                <Box maxWidth="min(800px, 100%)">
                  <Box mb="spacing8">Current layout</Box>
                  <AppLayoutPreview layoutSpec={initialLayoutSpec} />
                </Box>
              </VStack>
            ) : (
              <>
                <Flex
                  px="spacing16"
                  pb="spacing8"
                  pt="spacing24"
                  position="sticky"
                  top="0"
                  background="backgroundContent"
                  boxShadow={isScrolled ? '0 0 10px rgba(0, 0, 0, 0.1)' : 'none'}
                  zIndex="1"
                  alignItems="center"
                  justifyContent="space-between"
                  gap="spacing16"
                >
                  <Text color="colorTextImportant" fontSize="fontSizeLarge">
                    Select a template
                  </Text>
                  <ToggleGroup
                    data-testid="manage-space-template-filter"
                    defaultSelectedVariant={ButtonVariants.Priority}
                    value={templateFilter}
                    size={FormControlSizes.Small}
                    onChange={value => setTemplateFilter(value as TemplateFilter)}
                    options={[
                      { value: 'all', displayName: 'All', tooltip: 'Default, my own and shared templates' },
                      { value: 'own', displayName: 'My Own', tooltip: 'My own templates' },
                      { value: 'shared', displayName: 'Shared', tooltip: 'Templates shared with me' },
                      ...(canListAllSpaces
                        ? [
                            {
                              value: 'allUsers' as const,
                              displayName: 'All Users',
                              tooltip: "All users' templates (admin only)",
                            },
                          ]
                        : []),
                    ]}
                  />
                </Flex>
                <Grid
                  p="spacing8"
                  gap="spacing8"
                  gridTemplateColumns="repeat(auto-fit, minmax(280px, 1fr))"
                  data-testid="template-grid"
                >
                  {templateFilter === 'all' && copyForTemplateGrid && (
                    <Box position="relative">
                      <LayoutOptionWrapper
                        data-testid="template-option-copy"
                        onClick={() => handleSelectTemplate(copyForTemplateGrid)}
                        selected={selectedTemplate.id === 'copy'}
                      >
                        <Text>Copy</Text>
                        <AppLayoutPreview layoutSpec={copyForTemplateGrid} />
                      </LayoutOptionWrapper>
                      <LayoutOptionType>
                        <Tooltip tooltip="Copy of the current layout">
                          <IconButton icon={IconName.Duplicate} disabled ghost />
                        </Tooltip>
                      </LayoutOptionType>
                    </Box>
                  )}
                  {templateFilter === 'all' &&
                    visibleDefaultLayouts.map(template => (
                      <Box key={template.id} position="relative">
                        <LayoutOptionWrapper
                          data-testid={`template-option-${template.id}`}
                          onClick={() => handleSelectTemplate(template)}
                          selected={selectedTemplate.id === template.id}
                        >
                          <Text>{template.label}</Text>
                          {template.dockViewLayout && <AppLayoutPreview layoutSpec={template} />}
                        </LayoutOptionWrapper>
                        <LayoutOptionType>
                          <Tooltip tooltip="Standard template" placement="top">
                            <IconButton icon={IconName.Globe} disabled ghost />
                          </Tooltip>
                        </LayoutOptionType>
                      </Box>
                    ))}
                  {filteredSharedLayoutSpecs.map(config => (
                    <Box key={config.ConfigID} position="relative">
                      <LayoutOptionWrapper
                        data-testid={`template-option-${config.Config.id}`}
                        onClick={() => handleSelectTemplate(config.Config.layoutSpec)}
                        selected={selectedTemplate.id === config.Config.id}
                      >
                        <Text>{config.Config.label}</Text>
                        <AppLayoutPreview layoutSpec={config.Config.layoutSpec} />
                      </LayoutOptionWrapper>
                      <LayoutOptionType>
                        <Tooltip
                          tooltip={
                            config.User === user.Name
                              ? 'Edit template'
                              : canEditAllSpaces
                                ? 'Edit shared template'
                                : 'Shared template'
                          }
                        >
                          <IconButton
                            data-testid={`template-edit-${config.Config.id}`}
                            onClick={() => handleEditTemplate(config)}
                            icon={config.User === user.Name ? IconName.PencilField : IconName.Users}
                            ghost
                            disabled={!canEditAllSpaces && config.User !== user.Name}
                          />
                        </Tooltip>
                      </LayoutOptionType>
                    </Box>
                  ))}
                </Grid>
              </>
            )}
          </Stack>
        </Flex>
        <DialogFooterWrapper flexOrder="3">
          <Button data-testid="manage-space-cancel" onClick={disclosure.close}>
            Cancel
          </Button>
          <Box onMouseEnter={validateSpaceName}>
            <Button
              data-testid={isEditMode ? 'edit-space-save' : 'manage-space-create'}
              type="submit"
              form="manage-space-form"
              variant={ButtonVariants.Positive}
              disabled={spaceNameError || spaceNameDuplicate || groupNameInvalid}
            >
              {isEditMode ? 'Update space' : 'Add new space'}
            </Button>
          </Box>
        </DialogFooterWrapper>
        <PopperHeader
          icon={IconName.Template}
          titlePrefix={<LayoutSectionBreadcrumb layoutSection={layoutSection} />}
          title={isEditMode ? 'Edit space' : 'Add new space'}
          onCloseClicked={disclosure.close}
        />
      </Modal>
    </>
  );
}

function createLayoutCopy(layoutSpec: LayoutSpec | undefined): LayoutSpec | undefined {
  if (!layoutSpec) {
    return undefined;
  }
  return structuredClone({ ...layoutSpec, id: 'copy', templateId: layoutSpec.templateId ?? layoutSpec.id });
}
